<template>
  <Page>
    <div class="flex h-full flex-col gap-3">
      <MaterialsSearchForm
        :initial-filters="filters"
        :loading="loading"
        @submit="updateFilters"
        @reset="resetFilters"
      />

      <MaterialsToolbar
        v-model:multi-select-enabled="multiSelectEnabled"
        :selection="selectedItems"
        :loading="actionLoading"
        :readonly="isReadOnly"
        @add="openAdd"
        @edit="openEdit"
        @clone="openClone"
        @delete="confirmBulkDelete"
        @restore="confirmBulkRestore"
        @view="openView"
      />

      <div class="relative flex min-h-80 flex-1 flex-col overflow-hidden rounded-lg border border-border bg-background-card shadow-card">
        <div v-if="loading && !items.length" class="flex flex-1 items-center justify-center px-6 py-8 text-sm text-text-light">
          Loading materials…
        </div>

        <div v-else-if="error" class="flex flex-1 items-center justify-center px-6 py-8 text-center text-sm text-error">
          {{ error }}
        </div>

        <div v-else-if="!items.length" class="flex flex-1 items-center justify-center px-6 py-10">
          <div class="max-w-md text-center">
            <h2 class="text-base font-semibold text-text">No materials found</h2>
            <p class="mt-1 text-sm text-text-light">
              {{ hasActiveFilters ? 'Try adjusting or clearing your filters.' : 'Add a material to get started.' }}
            </p>
            <button v-if="!hasActiveFilters && !isReadOnly" class="btn-primary mt-4" type="button" @click="openAdd">
              <i class="fa-solid fa-plus mr-2" aria-hidden="true"></i>
              Add Material
            </button>
          </div>
        </div>

        <div v-else class="flex-1 flex flex-col overflow-hidden">
          <div v-if="isReadOnly && items.length" class="flex items-center gap-2 px-4 py-1.5 bg-background-elevated border-b border-border text-xs text-text-light">
            <i class="fa-solid fa-circle-info text-accent" aria-hidden="true"></i>
            <span>Tip: Double-click a row to view its details.</span>
          </div>
          <MaterialsTable
            v-model:selection="selectedItems"
            :rows="items"
            :loading="loading"
            :sort-field="sortState.field"
            :sort-order="sortState.order"
            :multi-select-enabled="multiSelectEnabled"
            :readonly="isReadOnly"
            @sort="onSort"
            @view="openView"
          />
          <Paginator
            :current-page="page.page"
            :page-count="pageCount"
            :page-size="page.pageSize"
            :total-items="page.totalItems ?? items.length"
            item-label="materials"
            @update:currentPage="handlePageChange"
            @first="() => handlePageChange(1)"
            @prev="() => handlePageChange(Math.max(1, page.page - 1))"
            @next="() => handlePageChange(Math.min(pageCount, page.page + 1))"
            @last="() => handlePageChange(pageCount)"
          />
        </div>
      </div>
    </div>

    <CloneMaterialModal
      :visible="cloneVisible"
      :material="cloneTarget"
      :loading="actionLoading"
      @close="closeClone"
      @confirm="confirmClone"
    />

    <MaterialFormPanel
      :visible="panelVisible"
      :mode="panelMode === 'view' ? 'edit' : panelMode"
      :material-id="panelMaterialId"
      :readonly="panelMode === 'view'"
      @close="closePanel"
      @saved="handleSaved"
    />
  </Page>
</template>

<script setup lang="ts">
import { computed, onActivated, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useConfirm } from 'primevue/useconfirm';
import { useToast } from 'primevue/usetoast';
import { addToast } from '@/shared/utils/toastMessages';
import Page from '@/shared/components/layouts/Page.vue';
import Paginator from '@/shared/components/ui/Paginator.vue';
import MaterialsSearchForm from '../components/materials/MaterialsSearchForm.vue';
import MaterialsTable from '../components/materials/MaterialsTable.vue';
import MaterialsToolbar from '../components/materials/MaterialsToolbar.vue';
import CloneMaterialModal from '../components/materials/CloneMaterialModal.vue';
import MaterialFormPanel from '../components/materials/MaterialFormPanel.vue';
import { useMaterials } from '../composables/useMaterials';
import { useUserRole } from '@/shared/composables/useUserRole';
import { isProjectViewerRole } from '@/shared/utils/projectAccess';
import type { MaterialDto } from '@/shared/services/materials';

definePage({
  meta: {
    title: 'Materials',
  },
});

const router = useRouter();
const route = useRoute();
const confirm = useConfirm();
const toast = useToast();

const { userRole } = useUserRole();
const isReadOnly = computed(() => isProjectViewerRole(userRole.value));

const {
  items,
  loading,
  error,
  page,
  filters,
  sortState,
  hasActiveFilters,
  actionLoading,
  selectedItems,
  fetchMaterials,
  updateFilters,
  resetFilters,
  handlePageChange,
  handleSortChange,
  deleteMaterial,
  bulkDeleteMaterials,
  restoreMaterial,
  bulkRestoreMaterials,
  cloneMaterial,
  resetListState,
} = useMaterials();

const cloneVisible = ref(false);
const cloneTarget = ref<MaterialDto | null>(null);

const multiSelectEnabled = ref(false);

const panelVisible = computed(() => route.query.mode === 'view' || (!isReadOnly.value && (route.query.mode === 'add' || route.query.mode === 'edit')));
const panelMode = computed<'add' | 'edit' | 'view'>(() => {
  if (route.query.mode === 'view') return 'view';
  return route.query.mode === 'edit' ? 'edit' : 'add';
});
const panelMaterialId = computed(() => (typeof route.query.materialId === 'string' ? route.query.materialId : null));

const pageCount = computed(() => page.value.totalPages ?? Math.max(1, Math.ceil((page.value.totalItems ?? 0) / page.value.pageSize)));

onMounted(() => {
  resetListState();
  void fetchMaterials();
});

onActivated(() => {
  resetListState();
  void fetchMaterials();
});

function openAdd(): void {
  if (isReadOnly.value) return;
  router.push({ query: { ...route.query, mode: 'add', materialId: undefined } });
}

function openEdit(material: MaterialDto): void {
  if (isReadOnly.value) return;
  if (!material.id) return;
  router.push({ query: { ...route.query, mode: 'edit', materialId: material.id } });
}

function openView(material: MaterialDto): void {
  if (!material.id) return;
  router.push({ query: { ...route.query, mode: 'view', materialId: material.id } });
}

function closePanel(): void {
  const query = { ...route.query };
  delete query.mode;
  delete query.materialId;
  router.push({ query });
}

async function handleSaved(): Promise<void> {
  addToast(toast, { severity: 'success', summary: 'Saved', detail: 'Material saved successfully' });
  closePanel();
  await fetchMaterials();
}

function onSort(payload: { field?: string; order?: 1 | -1 | 0 }): void {
  handleSortChange(payload.field, payload.order === 0 ? undefined : (payload.order as 1 | -1 | undefined));
}

function openClone(material: MaterialDto): void {
  cloneTarget.value = material;
  cloneVisible.value = true;
}

function closeClone(): void {
  cloneVisible.value = false;
  cloneTarget.value = null;
}

async function confirmClone(newId: string): Promise<void> {
  if (!cloneTarget.value?.id) return;
  try {
    await cloneMaterial(cloneTarget.value.id, newId);
    addToast(toast, { severity: 'success', summary: 'Cloned', detail: 'Material cloned successfully' });
  } catch (err) {
    const detail = err instanceof Error ? err.message : 'Failed to clone material';
    addToast(toast, { severity: 'error', summary: 'Clone failed', detail });
  } finally {
    closeClone();
  }
}

function confirmBulkDelete(materials: MaterialDto[]): void {
  if (materials.length === 0) return;

  const count = materials.length;
  const isSingle = count === 1;
  const firstMaterial = materials[0];
  const label = isSingle
    ? (firstMaterial?.description ?? firstMaterial?.id ?? 'material')
    : `${count} materials`;

  confirm.require({
    header: isSingle ? 'Delete Material' : 'Delete Materials',
    message: isSingle
      ? `Are you sure you want to delete "${label}"?`
      : `Are you sure you want to delete ${label}? This action will delete all selected materials.`,
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: isSingle ? 'Delete' : `Delete ${count}`,
    rejectLabel: 'Cancel',
    acceptClass: 'p-button-danger',
    accept: async () => {
      if (isSingle && firstMaterial?.id) {
        await deleteMaterial(firstMaterial.id);
        addToast(toast, { severity: 'success', summary: 'Deleted', detail: 'Material deleted' });
      } else {
        const ids = materials.map((m) => m.id).filter((id): id is string => Boolean(id));
        const result = await bulkDeleteMaterials(ids);
        if (result.failed === 0) {
          addToast(toast, {
            severity: 'success',
            summary: 'Deleted',
            detail: `${result.success} material${result.success > 1 ? 's' : ''} deleted successfully`,
          });
        } else {
          addToast(toast, {
            severity: 'warn',
            summary: 'Partial Success',
            detail: `${result.success} deleted, ${result.failed} failed`,
            life: 5000,
          });
        }
      }
    },
  });
}

function confirmBulkRestore(materials: MaterialDto[]): void {
  if (materials.length === 0) return;

  const count = materials.length;
  const isSingle = count === 1;
  const firstMaterial = materials[0];
  const label = isSingle
    ? (firstMaterial?.description ?? firstMaterial?.id ?? 'material')
    : `${count} materials`;

  confirm.require({
    header: isSingle ? 'Restore Material' : 'Restore Materials',
    message: isSingle
      ? `Are you sure you want to restore "${label}"?`
      : `Are you sure you want to restore ${label}?`,
    icon: 'pi pi-undo',
    acceptLabel: isSingle ? 'Restore' : `Restore ${count}`,
    rejectLabel: 'Cancel',
    accept: async () => {
      if (isSingle && firstMaterial?.id) {
        await restoreMaterial(firstMaterial.id);
        addToast(toast, { severity: 'success', summary: 'Restored', detail: 'Material restored' });
      } else {
        const ids = materials.map((m) => m.id).filter((id): id is string => Boolean(id));
        const result = await bulkRestoreMaterials(ids);
        if (result.failed === 0) {
          addToast(toast, {
            severity: 'success',
            summary: 'Restored',
            detail: `${result.success} material${result.success > 1 ? 's' : ''} restored successfully`,
          });
        } else {
          addToast(toast, {
            severity: 'warn',
            summary: 'Partial Success',
            detail: `${result.success} restored, ${result.failed} failed`,
            life: 5000,
          });
        }
      }
    },
  });
}
</script>

<style scoped>
@reference '../../../style.css';
.btn-primary {
  @apply inline-flex items-center justify-center rounded-full bg-primary px-4 py-2 text-sm font-semibold text-text-inverse shadow-sm hover:bg-primary-dark transition disabled:cursor-not-allowed disabled:opacity-60;
}
</style>
